import type { BadgeVariant } from "@/components/ui/badge/badge.variants";

export enum ENUM_BOARD_TYPE {
  PROJECT = "project",
}

export enum ENUM_BOARD_METHODOLOGY {
  KANBAN = "kanban",
  SCRUM = "scrum",
}

export enum ENUM_SPRINT_STATUS {
  PLANNED = "planned",
  ACTIVE = "active",
  COMPLETED = "completed",
}

export enum ENUM_TASK_TYPE {
  STORY = "story",
  TASK = "task",
  BUG = "bug",
  EPIC = "epic",
  SUBTASK = "subtask",
}

export const TASK_TYPE_LABEL: Record<ENUM_TASK_TYPE, string> = {
  [ENUM_TASK_TYPE.STORY]: "Story",
  [ENUM_TASK_TYPE.TASK]: "Task",
  [ENUM_TASK_TYPE.BUG]: "Bug",
  [ENUM_TASK_TYPE.EPIC]: "Epic",
  [ENUM_TASK_TYPE.SUBTASK]: "Subtask",
};

/**
 * Task type → label + token colors (Jira-style square icons).
 * `text` is the saturated glyph color (used on light surfaces); `solid` is the
 * filled background for the colored type square (white glyph sits on top).
 */
export const TASK_TYPE_META: Record<
  ENUM_TASK_TYPE,
  { label: string; text: string; solid: string }
> = {
  [ENUM_TASK_TYPE.STORY]: {
    label: "Story",
    text: "text-success",
    solid: "bg-success",
  },
  [ENUM_TASK_TYPE.TASK]: {
    label: "Task",
    text: "text-info",
    solid: "bg-info",
  },
  [ENUM_TASK_TYPE.BUG]: {
    label: "Bug",
    text: "text-danger",
    solid: "bg-danger",
  },
  [ENUM_TASK_TYPE.EPIC]: {
    label: "Epic",
    text: "text-primary",
    solid: "bg-primary",
  },
  [ENUM_TASK_TYPE.SUBTASK]: {
    label: "Subtask",
    text: "text-muted-foreground",
    solid: "bg-muted-foreground",
  },
};

export const TASK_TYPE_OPTIONS = (
  Object.values(ENUM_TASK_TYPE) as ENUM_TASK_TYPE[]
)
  .filter((t) => t !== ENUM_TASK_TYPE.SUBTASK)
  .map((value) => ({ value, label: TASK_TYPE_LABEL[value] }));

export enum ENUM_BOARD_VISIBILITY {
  COMPANY = "company",
  TEAM = "team",
}

export enum ENUM_TASK_PRIORITY {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  URGENT = "urgent",
}

export enum ENUM_TASK_LABEL_COLOR {
  GRAY = "gray",
  BLUE = "blue",
  GREEN = "green",
  AMBER = "amber",
  RED = "red",
  PURPLE = "purple",
}

export enum ENUM_TASK_RESOLUTION {
  DONE = "done",
  WONT_DO = "wont_do",
  DUPLICATE = "duplicate",
}

export const TASK_RESOLUTION_LABEL: Record<ENUM_TASK_RESOLUTION, string> = {
  [ENUM_TASK_RESOLUTION.DONE]: "Done",
  [ENUM_TASK_RESOLUTION.WONT_DO]: "Won't Do",
  [ENUM_TASK_RESOLUTION.DUPLICATE]: "Duplicate",
};

export const TASK_RESOLUTION_OPTIONS = Object.values(ENUM_TASK_RESOLUTION).map(
  (value) => ({ value, label: TASK_RESOLUTION_LABEL[value] }),
);

export const TASK_RESOLUTION_VARIANT: Record<
  ENUM_TASK_RESOLUTION,
  BadgeVariant
> = {
  [ENUM_TASK_RESOLUTION.DONE]: "success",
  [ENUM_TASK_RESOLUTION.WONT_DO]: "neutral",
  [ENUM_TASK_RESOLUTION.DUPLICATE]: "warning",
};

export enum ENUM_TASK_LINK_TYPE {
  BLOCKS = "blocks",
  RELATES = "relates",
  DUPLICATES = "duplicates",
}

/** Link-type options for the "add link" picker (verb shown to the user). */
export const TASK_LINK_TYPE_OPTIONS: { value: ENUM_TASK_LINK_TYPE; label: string }[] =
  [
    { value: ENUM_TASK_LINK_TYPE.BLOCKS, label: "blocks" },
    { value: ENUM_TASK_LINK_TYPE.RELATES, label: "relates to" },
    { value: ENUM_TASK_LINK_TYPE.DUPLICATES, label: "duplicates" },
  ];

/** Normalized relationship key (from backend) → human label + grouping. */
export const TASK_LINK_RELATIONSHIP_LABEL: Record<string, string> = {
  blocks: "blocks",
  is_blocked_by: "is blocked by",
  duplicates: "duplicates",
  is_duplicated_by: "is duplicated by",
  relates: "relates to",
};

/** Priority → Badge variant (canonical Badge component). */
export const TASK_PRIORITY_VARIANT: Record<ENUM_TASK_PRIORITY, BadgeVariant> = {
  [ENUM_TASK_PRIORITY.LOW]: "neutral",
  [ENUM_TASK_PRIORITY.MEDIUM]: "info",
  [ENUM_TASK_PRIORITY.HIGH]: "warning",
  [ENUM_TASK_PRIORITY.URGENT]: "danger",
};

export const TASK_PRIORITY_LABEL: Record<ENUM_TASK_PRIORITY, string> = {
  [ENUM_TASK_PRIORITY.LOW]: "Low",
  [ENUM_TASK_PRIORITY.MEDIUM]: "Medium",
  [ENUM_TASK_PRIORITY.HIGH]: "High",
  [ENUM_TASK_PRIORITY.URGENT]: "Urgent",
};

export const TASK_PRIORITY_OPTIONS = Object.values(ENUM_TASK_PRIORITY).map(
  (value) => ({ value, label: TASK_PRIORITY_LABEL[value] }),
);

/**
 * Board column / label palette → semantic token classes.
 * Columns carry a free `color` name (not a request status), so this is a fixed
 * palette selector, not a status→color map. Token-pure per the design system.
 */
export const TASK_COLOR_TOKEN: Record<
  ENUM_TASK_LABEL_COLOR,
  { dot: string; soft: string; text: string }
> = {
  [ENUM_TASK_LABEL_COLOR.GRAY]: {
    dot: "bg-muted-foreground",
    soft: "bg-muted",
    text: "text-muted-foreground",
  },
  [ENUM_TASK_LABEL_COLOR.BLUE]: {
    dot: "bg-info",
    soft: "bg-info-muted",
    text: "text-info",
  },
  [ENUM_TASK_LABEL_COLOR.GREEN]: {
    dot: "bg-success",
    soft: "bg-success-muted",
    text: "text-success",
  },
  [ENUM_TASK_LABEL_COLOR.AMBER]: {
    dot: "bg-warning",
    soft: "bg-warning-muted",
    text: "text-warning",
  },
  [ENUM_TASK_LABEL_COLOR.RED]: {
    dot: "bg-danger",
    soft: "bg-danger-muted",
    text: "text-danger",
  },
  [ENUM_TASK_LABEL_COLOR.PURPLE]: {
    dot: "bg-primary",
    soft: "bg-primary-muted",
    text: "text-primary",
  },
};

export const TASK_COLOR_OPTIONS = Object.values(ENUM_TASK_LABEL_COLOR).map(
  (value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }),
);

/** Safe lookup for a possibly-unknown color string. */
export function taskColorToken(color?: string | null) {
  const key = (color ?? ENUM_TASK_LABEL_COLOR.GRAY) as ENUM_TASK_LABEL_COLOR;
  return TASK_COLOR_TOKEN[key] ?? TASK_COLOR_TOKEN[ENUM_TASK_LABEL_COLOR.GRAY];
}

/**
 * Tasks visible on a board's "Board" tab.
 *
 * A Scrum board only shows the issues in its active sprint (an empty board when
 * no sprint is running); a Kanban board shows everything. Single source of truth
 * so the company and my-space board pages can't drift apart. The Backlog tab is
 * intentionally NOT filtered through this — it needs every sprint + the backlog.
 *
 * Structurally typed (no ITaskBoard/ISingleTask import) to avoid a circular
 * dependency with the tasks interface module, which imports the enums here.
 */
export const visibleBoardTasks = <
  T extends { sprintId: number | null; completedAt?: string | null },
>(
  methodology: ENUM_BOARD_METHODOLOGY | undefined,
  tasks: T[],
  activeSprintId: number | null | undefined,
  // Kanban only: hide Done issues completed more than this many days ago.
  // null/undefined = show all completed issues.
  doneDisplayDays?: number | null,
): T[] => {
  // Scrum sweeps done issues via sprint completion — show only the active sprint.
  if (methodology === ENUM_BOARD_METHODOLOGY.SCRUM) {
    return tasks.filter((t) => t.sprintId === activeSprintId);
  }
  // Kanban: optionally drop stale completed issues (Jira "hide done older than N").
  if (doneDisplayDays == null) return tasks;
  const cutoff = Date.now() - doneDisplayDays * 24 * 60 * 60 * 1000;
  return tasks.filter(
    (t) => !t.completedAt || new Date(t.completedAt).getTime() >= cutoff,
  );
};
